/* eslint-disable @typescript-eslint/no-explicit-any */
import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse, AxiosError, InternalAxiosRequestConfig } from 'axios';

/**
 * 扩展axios配置接口，添加metadata属性
 */
declare module 'axios' {
  interface InternalAxiosRequestConfig {
    metadata?: {
      startTime: number;
    };
  }
}

/**
 * HTTP请求配置接口
 */
export interface HttpConfig extends AxiosRequestConfig {
  timeout?: number;
  retryCount?: number;
  retryDelay?: number;
}

/**
 * HTTP响应接口
 */
export interface HttpResponse<T = any> {
  data: T;
  code: number;
  message: string;
  success: boolean;
}

/**
 * HTTP请求库类
 * 遵循单一职责原则，专门负责HTTP请求的封装和处理
 */
export class HttpClient {
  private instance: AxiosInstance;
  private defaultConfig: HttpConfig;

  constructor(config: HttpConfig = {}) {
    this.defaultConfig = {
      timeout: 10000,
      retryCount: 3,
      retryDelay: 1000,
      ...config
    };

    this.instance = axios.create(this.defaultConfig);
    this.setupInterceptors();
  }

  /**
   * 设置请求和响应拦截器
   * 遵循开闭原则，可以扩展但不修改核心逻辑
   */
  private setupInterceptors(): void {
    // 请求拦截器
    this.instance.interceptors.request.use(
      (config: InternalAxiosRequestConfig) => {
        // 添加请求时间戳
        config.metadata = { startTime: Date.now() };
        
        // 可以在这里添加认证token等
        const token = this.getAuthToken();
        if (token && config.headers) {
          config.headers.Authorization = `Bearer ${token}`;
        }

        return config;
      },
      (error: AxiosError) => {
        return Promise.reject(this.handleError(error));
      }
    );

    // 响应拦截器
    this.instance.interceptors.response.use(
      (response: AxiosResponse) => {
        // 计算请求耗时
        const endTime = Date.now();
        const startTime = response.config.metadata?.startTime || endTime;
        const duration = endTime - startTime;

        // 可以在这里添加响应日志
        this.logResponse(response, duration);

        return response;
      },
      (error: AxiosError) => {
        return Promise.reject(this.handleError(error));
      }
    );
  }

  /**
   * 获取认证token
   * 遵循依赖倒置原则，可以通过配置或外部注入
   */
  private getAuthToken(): string | null {
    // 可以从localStorage、sessionStorage或其他地方获取token
    return localStorage.getItem('auth_token');
  }

  /**
   * 记录响应日志
   */
  private logResponse(_response: AxiosResponse, _duration: number): void {
    if (process.env.NODE_ENV === 'development') {
      // HTTP请求完成
    }
  }

  /**
   * 统一错误处理
   * 遵循单一职责原则，专门处理错误
   */
  private handleError(error: AxiosError): Error {
    const errorInfo = {
      message: '请求失败',
      code: 0,
      status: error.response?.status,
      data: error.response?.data
    };

    if (error.response) {
      // 服务器响应错误
      switch (error.response.status) {
        case 400:
          errorInfo.message = '请求参数错误';
          break;
        case 401:
          errorInfo.message = '未授权访问';
          break;
        case 403:
          errorInfo.message = '禁止访问';
          break;
        case 404:
          errorInfo.message = '请求资源不存在';
          break;
        case 500:
          errorInfo.message = '服务器内部错误';
          break;
        default:
          errorInfo.message = `请求失败 (${error.response.status})`;
      }
    } else if (error.request) {
      // 网络错误
      errorInfo.message = '网络连接失败';
    } else {
      // 其他错误
      errorInfo.message = error.message || '未知错误';
    }

    return new Error(JSON.stringify(errorInfo));
  }

  /**
   * GET请求
   */
  public async get<T = any>(url: string, config?: HttpConfig): Promise<HttpResponse<T>> {
    const response = await this.instance.get(url, config);
    return this.formatResponse<T>(response);
  }

  /**
   * POST请求
   */
  public async post<T = any>(url: string, data?: any, config?: HttpConfig): Promise<HttpResponse<T>> {
    const response = await this.instance.post(url, data, config);
    return this.formatResponse<T>(response);
  }

  /**
   * PUT请求
   */
  public async put<T = any>(url: string, data?: any, config?: HttpConfig): Promise<HttpResponse<T>> {
    const response = await this.instance.put(url, data, config);
    return this.formatResponse<T>(response);
  }

  /**
   * DELETE请求
   */
  public async delete<T = any>(url: string, config?: HttpConfig): Promise<HttpResponse<T>> {
    const response = await this.instance.delete(url, config);
    return this.formatResponse<T>(response);
  }

  /**
   * 格式化响应数据
   * 遵循DRY原则，避免重复代码
   */
  private formatResponse<T>(response: AxiosResponse): HttpResponse<T> {
    return {
      data: response.data,
      code: response.status,
      message: response.statusText,
      success: response.status >= 200 && response.status < 300
    };
  }

  /**
   * 设置默认配置
   */
  public setDefaultConfig(config: Partial<HttpConfig>): void {
    Object.assign(this.defaultConfig, config);
    // 只更新非headers的配置
    const { headers, ...restConfig } = config;
    Object.assign(this.instance.defaults, restConfig);
    
    // 单独处理headers
    if (headers && this.instance.defaults.headers) {
      Object.assign(this.instance.defaults.headers.common, headers);
    }
  }

  /**
   * 获取axios实例（用于特殊需求）
   */
  public getInstance(): AxiosInstance {
    return this.instance;
  }
}

// 创建默认的HTTP客户端实例
export const httpClient = new HttpClient();

// 导出便捷方法
export const http = {
  get: <T = any>(url: string, config?: HttpConfig) => httpClient.get<T>(url, config),
  post: <T = any>(url: string, data?: any, config?: HttpConfig) => httpClient.post<T>(url, data, config),
  put: <T = any>(url: string, data?: any, config?: HttpConfig) => httpClient.put<T>(url, data, config),
  delete: <T = any>(url: string, config?: HttpConfig) => httpClient.delete<T>(url, config)
}; 